import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { productRepository } from "../product.repository";
import { productCategoriesRepository } from "../product-categories.repository";
import { productSupplierRepository } from "../product-supplier.repository";
import type { ProductCategory } from "../product-category.entity";
import type { ProductSupplier } from "../product-supplier.entity";
import type { Product } from "../product.entity";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";

type StockFilter = "all" | "zero" | "low" | "ok" | "shortage";

function matchesStockLevelFilter(
  p: Product,
  filter: StockFilter,
  lowThreshold: number
): boolean {
  const q = p.getStock();
  switch (filter) {
    case "all":
      return true;
    case "zero":
      return q <= 0;
    case "low":
      return q > 0 && q <= lowThreshold;
    case "ok":
      return q > lowThreshold;
    case "shortage":
      return q <= lowThreshold;
    default:
      return true;
  }
}

function matchesSupplierFilter(p: Product, supplierId: string): boolean {
  if (!supplierId) return true;
  return p.getSulier()?.id === supplierId;
}

function matchesCategoryFilter(p: Product, categoryId: string): boolean {
  if (!categoryId) return true;
  return p.getCategory()?.id === categoryId;
}

function statusFor(
  p: Product,
  lowThreshold: number
): "sem_controle" | "zerado" | "baixo" | "ok" {
  if (!p.getTrackStock()) return "sem_controle";
  const q = p.getStock();
  if (q <= 0) return "zerado";
  if (q <= lowThreshold) return "baixo";
  return "ok";
}

export const InventoryPage = () => {
  const theme = useTheme();
  const [user] = application.user.useState();
  application.useHasPermission([Permission.SELL, Permission.WRITE_PRODUCTS]);

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [suppliers, setSuppliers] = useState<ProductSupplier[]>([]);
  const [filter, setFilter] = useState<StockFilter>("all");
  const [lowThreshold, setLowThreshold] = useState(5);
  const [supplierId, setSupplierId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const moduleAccess = useModuleAccess();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [list, cats, sups] = await Promise.all([
          productRepository.getAll("name"),
          productCategoriesRepository.getAll(),
          productSupplierRepository.getAll(),
        ]);
        if (!cancelled) {
          setProducts(list);
          setCategories(cats);
          setSuppliers(sups);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const tracked = useMemo(
    () => products.filter((p) => p.getTrackStock()),
    [products]
  );
  const untracked = useMemo(
    () => products.filter((p) => !p.getTrackStock()),
    [products]
  );

  const stats = useMemo(() => {
    let zerados = 0;
    let baixos = 0;
    let ok = 0;
    let units = 0;
    for (const p of tracked) {
      const q = p.getStock();
      units += q;
      const st = statusFor(p, lowThreshold);
      if (st === "zerado") zerados++;
      else if (st === "baixo") baixos++;
      else ok++;
    }
    return {
      skus: tracked.length,
      units,
      zerados,
      baixos,
      ok,
      untrackedCount: untracked.length,
    };
  }, [tracked, untracked.length, lowThreshold]);

  const rows = useMemo(() => {
    return tracked.filter(
      (p) =>
        matchesStockLevelFilter(p, filter, lowThreshold) &&
        matchesSupplierFilter(p, supplierId) &&
        matchesCategoryFilter(p, categoryId)
    );
  }, [tracked, filter, lowThreshold, supplierId, categoryId]);

  const untrackedFiltered = useMemo(() => {
    return untracked.filter(
      (p) => matchesSupplierFilter(p, supplierId) && matchesCategoryFilter(p, categoryId)
    );
  }, [untracked, supplierId, categoryId]);

  const hasDimensionFilters = Boolean(supplierId || categoryId);

  const canEdit = user?.hasPermission([Permission.WRITE_PRODUCTS]);

  if (moduleAccess.loading) {
    return (
      <Shell title="Estoque">
        <LoadingOverlay message="Carregando módulo…" subtle />
      </Shell>
    );
  }

  if (!moduleAccess.isEnabled("inventory_control")) {
    return (
      <Shell title="Estoque">
        <PageHeader title="Estoque" description="Controle detalhado de estoque." />
        <ModuleLockedView
          moduleName="Controle de estoque"
          groupId="inventory_control"
          details="Ative este módulo para acompanhar saldo, ruptura e alertas de reposição."
          features={["Saldo em tempo real por produto", "Alertas de ruptura e reposição", "Histórico de movimentações"]}
        />
      </Shell>
    );
  }

  return (
    <Shell title="Estoque">
      {loading ? <LoadingOverlay message="Carregando produtos…" subtle /> : null}

      <PageHeader
        title="Estoque"
        description="Acompanhe quantidades, itens em falta e produtos sem controle de estoque. Na venda, se acabar o estoque, o saldo não fica negativo — permanece em zero. Ajuste cada item no cadastro do produto."
        action={
          canEdit ? (
            <Button variant="outlined" component={RouterLink} to="/produtos">
              Lista de produtos
            </Button>
          ) : null
        }
      />

      <Stack spacing={3}>
        <PaperLikeHero stats={stats} lowThreshold={lowThreshold} />

        <Card elevation={0}>
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Alerta de estoque baixo
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <TextField
                type="number"
                size="small"
                label="Quantidade máxima para considerar &quot;baixo&quot;"
                value={lowThreshold}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setLowThreshold(Number.isNaN(v) || v < 0 ? 0 : v);
                }}
                inputProps={{ min: 0, step: 1 }}
                sx={{ maxWidth: 320 }}
                helperText="Ex.: 5 — itens com estoque entre 1 e 5 aparecem como baixos."
              />
            </Stack>
          </CardContent>
        </Card>

        <Card elevation={0}>
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Situação do estoque
            </Typography>
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={(_e, v: StockFilter | null) => {
                if (v !== null) setFilter(v);
              }}
              size="small"
              sx={{ flexWrap: "wrap", gap: 0.5, mb: 2 }}
            >
              <ToggleButton value="all">Todos (com controle)</ToggleButton>
              <ToggleButton value="shortage">Em falta (zerado ou baixo)</ToggleButton>
              <ToggleButton value="zero">Zerados</ToggleButton>
              <ToggleButton value="low">Só baixos</ToggleButton>
              <ToggleButton value="ok">Saudáveis</ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Fornecedor e categoria
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              flexWrap="wrap"
              useFlexGap
            >
              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 220 } }}>
                <InputLabel id="inv-supplier">Fornecedor</InputLabel>
                <Select
                  labelId="inv-supplier"
                  label="Fornecedor"
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Todos</em>
                  </MenuItem>
                  {suppliers.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 220 } }}>
                <InputLabel id="inv-category">Categoria</InputLabel>
                <Select
                  labelId="inv-category"
                  label="Categoria"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Todas</em>
                  </MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {hasDimensionFilters ? (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    setSupplierId("");
                    setCategoryId("");
                  }}
                  sx={{ alignSelf: { sm: "center" } }}
                >
                  Limpar fornecedor e categoria
                </Button>
              ) : null}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
              Combine com &quot;Zerados&quot; ou &quot;Em falta&quot; para ver rupturas por fornecedor (ex.: o que repor
              de cada um).
            </Typography>
          </CardContent>
        </Card>

        {stats.zerados > 0 ? (
          <Alert severity="warning" variant="outlined" icon={<WarningAmberOutlinedIcon />}>
            {stats.zerados} produto(s) com estoque zerado — repor ou revisar cadastros.
          </Alert>
        ) : null}

        <Card elevation={0}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Produtos com controle de estoque
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {rows.length} de {tracked.length} no filtro atual
                {hasDimensionFilters ? (
                  <>
                    {" "}
                    · fornecedor/categoria aplicados
                  </>
                ) : null}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                    <TableCell>Produto</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Fornecedor</TableCell>
                    <TableCell align="right">Qtd</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography color="text.secondary" sx={{ py: 3 }}>
                          Nenhum produto neste filtro.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((p) => {
                      const st = statusFor(p, lowThreshold);
                      const chip =
                        st === "zerado" ? (
                          <Chip size="small" color="error" label="Zerado" />
                        ) : st === "baixo" ? (
                          <Chip size="small" color="warning" label="Baixo" />
                        ) : (
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label="OK"
                          />
                        );
                      return (
                        <TableRow key={p.id} hover>
                          <TableCell>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              <Avatar
                                variant="rounded"
                                src={p.getImage()}
                                alt=""
                                sx={{
                                  width: 48,
                                  height: 48,
                                  flexShrink: 0,
                                  bgcolor: "action.hover",
                                  border: 1,
                                  borderColor: "divider",
                                }}
                              />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography fontWeight={600} noWrap title={p.getName() || ""}>
                                  {p.getName() || "Sem nome"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                  {p.getCodeLabelForUi()}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {p.getCategory()?.title ?? "—"}
                          </TableCell>
                          <TableCell>{p.getSulier()?.title ?? "—"}</TableCell>
                          <TableCell align="right">{p.getStock()}</TableCell>
                          <TableCell>{chip}</TableCell>
                          <TableCell align="right">
                            {canEdit ? (
                              <Button
                                size="small"
                                component={RouterLink}
                                to={`/produtos/editar/${p.id}`}
                              >
                                Editar
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {untracked.length > 0 ? (
          <Card elevation={0}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Sem controle de estoque
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {untrackedFiltered.length === untracked.length ?
                    `${untracked.length} item(ns)` :
                    `${untrackedFiltered.length} de ${untracked.length} item(ns) com os filtros atuais`}
                  {" "}
                  — vendas não alteram quantidade. Ative o controle no cadastro do produto se precisar.
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.08) }}>
                      <TableCell>Produto</TableCell>
                      <TableCell>Categoria</TableCell>
                      <TableCell>Fornecedor</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {untrackedFiltered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography color="text.secondary" sx={{ py: 2 }}>
                            Nenhum item sem controle neste recorte de fornecedor/categoria.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {untrackedFiltered.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Avatar
                              variant="rounded"
                              src={p.getImage()}
                              alt=""
                              sx={{
                                width: 48,
                                height: 48,
                                flexShrink: 0,
                                bgcolor: "action.hover",
                                border: 1,
                                borderColor: "divider",
                              }}
                            />
                            <Typography fontWeight={600} noWrap title={p.getName() || ""}>
                              {p.getName() || "Sem nome"}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{p.getCategory()?.title ?? "—"}</TableCell>
                        <TableCell>{p.getSulier()?.title ?? "—"}</TableCell>
                        <TableCell align="right">
                          {canEdit ? (
                            <Button
                              size="small"
                              component={RouterLink}
                              to={`/produtos/editar/${p.id}`}
                            >
                              Editar
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ) : null}
      </Stack>
    </Shell>
  );
};

function PaperLikeHero({
  stats,
  lowThreshold,
}: {
  stats: {
    skus: number;
    units: number;
    zerados: number;
    baixos: number;
    ok: number;
    untrackedCount: number;
  };
  lowThreshold: number;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(theme.palette.primary.main, 0.22),
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.1
        )} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.15),
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Inventory2OutlinedIcon />
        </Box>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.08 }}>
            Resumo
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            Visão geral do estoque
          </Typography>
        </Box>
      </Stack>
      <Grid container spacing={1.5}>
        <Grid item xs={6} sm={4} md={2}>
          <MiniStat label="SKUs controlados" value={String(stats.skus)} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MiniStat label="Unidades totais" value={String(stats.units)} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MiniStat
            label="Zerados"
            value={String(stats.zerados)}
            emphasize={stats.zerados > 0 ? "error" : undefined}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MiniStat
            label={`Baixos (1–${lowThreshold})`}
            value={String(stats.baixos)}
            emphasize={stats.baixos > 0 ? "warning" : undefined}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MiniStat label="OK" value={String(stats.ok)} icon={<CheckCircleOutlineIcon fontSize="small" />} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MiniStat label="Sem controle" value={String(stats.untrackedCount)} />
        </Grid>
      </Grid>
    </Box>
  );
}

function MiniStat({
  label,
  value,
  emphasize,
  icon,
}: {
  label: string;
  value: string;
  emphasize?: "error" | "warning";
  icon?: ReactNode;
}) {
  const color =
    emphasize === "error" ? "error.main" : emphasize === "warning" ? "warning.dark" : "text.primary";
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        height: "100%",
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {icon}
        <Typography variant="h6" fontWeight={800} sx={{ color }}>
          {value}
        </Typography>
      </Stack>
    </Box>
  );
}
